import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router";

import type {
  LanguageDto,
  TicketCategoryDetailsDto,
  UserRoleDto,
  UserRoleRepairCategoryPermissionsDto,
} from "@/api/types";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/components/FullSizeLoader/FullSizeLoader";
import { createFormTranslations, useLanguages } from "@/helpers/languages";
import { createFormPermissions } from "@/helpers/permissions";
import { usePermissionQueries } from "@/queries/permissions";
import { useTicketQueries } from "@/queries/tickets";

import type { LayoutProps, TicketCategoryDetailsFormValues } from "./Layout";
import { DAYS_VARIANTS } from "./Layout";
import { transformFormDataToAPI, useSubmitHandlers } from "./Manager";

interface Props {
  children: (props: LayoutProps) => React.ReactNode;
}

export function Loader({ children }: Props): React.ReactNode {
  const { categoryId, isEditMode } = usePageParams();

  const { createTicketCategory, editTicketCategory, isSubmitting } = useSubmitHandlers();
  const ticketQueries = useTicketQueries();
  const permissionQueries = usePermissionQueries();
  const {
    data: languages = [],
    isPending: isPendingLanguages,
    error: languagesError,
  } = useLanguages({
    onlyLegacyLanguages: true,
  });
  const {
    data: ticketCategory,
    isLoading: isLoadingTicketCategory,
    error: ticketCategoryError,
  } = useQuery({
    ...ticketQueries.categoryDetails({
      categoryId: categoryId!,
    }),
    enabled: isEditMode && Boolean(categoryId),
  });
  const {
    data: defaultsPermissions = [],
    isPending: isPendingDefaultPermissions,
    error: defaultPermissionsError,
  } = useQuery(permissionQueries.listDefaultTicketCategories());
  const { data: roles = [], isPending: isPendingRoles, error: rolesError } = useQuery(permissionQueries.listRoles());

  function onSubmit(formData: TicketCategoryDetailsFormValues) {
    const body = transformFormDataToAPI(formData);

    if (categoryId == null) {
      createTicketCategory(body);
    } else {
      editTicketCategory({ id: categoryId, body });
    }
  }

  const error = ticketCategoryError || languagesError || defaultPermissionsError || rolesError;
  if (error) {
    return <ErrorPage error={error} />;
  }

  if (isPendingLanguages || isPendingDefaultPermissions || isLoadingTicketCategory || isPendingRoles) {
    return <FullSizeLoader withPadding />;
  }

  return (
    <>
      {children({
        type: ticketCategory?.type || "single",
        languages,
        isEditMode,
        isSubmitting,
        onSubmit,
        defaultFormValues: getDefaultFormValues(roles, languages, defaultsPermissions, ticketCategory),
      })}
    </>
  );
}

function usePageParams() {
  const { id: categoryId } = useParams<{ id: string }>();

  return { categoryId, isEditMode: categoryId != null };
}

function getDefaultFormValues(
  roles: UserRoleDto[],
  languages: LanguageDto[],
  defaultPermissions: UserRoleRepairCategoryPermissionsDto[],
  ticketCategory?: TicketCategoryDetailsDto,
): TicketCategoryDetailsFormValues {
  return {
    translations: createFormTranslations({ languages, translations: ticketCategory?.translations }),
    defaultAssignee: ticketCategory?.defaultAssignee,
    notificationType: ticketCategory?.notificationType || "canBeCollective",
    closeAfterInactiveDayAmount: ticketCategory
      ? (String(ticketCategory.closeAfterInactiveDayAmount) as (typeof DAYS_VARIANTS)[number])
      : DAYS_VARIANTS[4],
    disabled: ticketCategory?.disabled ? "true" : "false",
    audience: ticketCategory?.audience || [],
    permissions: createFormPermissions(roles, defaultPermissions, ticketCategory?.permissions),
    reopenPolicy: ticketCategory?.reopenPolicy || "unlimited",
  };
}
